// External Packages
import { useState, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";

// Internal Packages
import { UtilityContext } from "../../context/util-context";
import { requestAdminAPI } from "../../api/posh-api";

// External Components
import { MdAdd, MdRefresh } from "react-icons/md";

// Internal Components
import DateFilter from "../UI/DateFilter";
import Heading from "../UI/Heading";
import PaginationMenu from "../UI/PaginationMenu";
import LoadingCard from "../UI/LoadingCard";
import NoRecordsFound from "../UI/NoRecordsFound";
import ExportCsvButton from "../UI/ExportCsvButton";
import { isoDateToString } from "../../util/format-date";
import ListLabels from "../UI/ListLabels";
import SearchBar from "../UI/SearchBar";

/** ========================================================================
 * * Quotes Page
 * Renders a page, which displays all quotes sent by admins.
 * ========================================================================== */

export default function Quotes() {
  const { navigate } = useContext(UtilityContext);
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  // Default Filters
  const defaultFilters = {
    sd: "",
    ed: "",
    query: "",
    limit: 50,
  };

  // Filters in URL params
  const paramFilters = {};
  Object.keys(defaultFilters).forEach(
    (key) => (paramFilters[key] = params.get(key) ?? defaultFilters[key])
  );

  // Initialize States
  const [quotes, setQuotes] = useState([]);
  const [filter, setFilter] = useState(paramFilters);
  const [loading, setLoading] = useState(true);

  /* ======================= FETCH RECORDS ======================= */
  const fetchRecords = async () => {
    try {
      setLoading(true);

      // Send Request
      const data = await requestAdminAPI("search", {
        table: "quote",
        limit: filter.limit,
        filters: filter
      });

      // Replace URL
      let url = "/quotes?";
      Object.keys(filter).forEach((key) => {
        url += `${key}=${filter[key] ?? defaultFilters[key]}&`;
      });
      navigate(url, {}, { replace: true });

      setQuotes(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // Apply Filters
  useEffect(() => {
    fetchRecords();
  }, [filter]);

  return (
    <div className="container grid">
      <Heading title="Quotes" isBackArrow={true}>
        <DateFilter
          startDate={filter.sd}
          endDate={filter.ed}
          onSubmit={(sd, ed) => setFilter({ ...filter, sd, ed })}
        />
        <MdAdd
          className="text-3xl icon"
          onClick={() => navigate("/quotes/add")}
          title="Add Quote"
        />
        <MdRefresh onClick={fetchRecords} className="icon" title="Refresh" />
        <ExportCsvButton array={quotes} filename="quotes" />
      </Heading>

      <SearchBar
        searchHandler={(query) => setFilter({ ...filter, query })}
        clearSearch={() => setFilter(defaultFilters)}
        searchValue={filter.query}
      />

      {loading ? (
        <LoadingCard columns={5} max={8} />
      ) : quotes.length > 0 ? (
        <div className="grid gap-2">
          <div className="labels lg:grid-cols-5">
            <ListLabels
              labels={[
                "Date Created",
                "Customer Name",
                "Email",
                "Phone",
                "Created By",
              ]}
            />
          </div>
          <div className="gap-2 list">
            {quotes.map((quote) => (
              <QuoteCard key={quote.id} quote={quote} />
            ))}
          </div>
        </div>
      ) : (
        <NoRecordsFound />
      )}

      <PaginationMenu
        limit={filter.limit}
        setLimit={(lim) => setFilter({ ...filter, limit: lim })}
        loading={loading}
        count={quotes.length}
      />
    </div>
  );
}

/** ========================================================================
 * * Quote Card
 * ========================================================================== */
const QuoteCard = ({ quote }) => {
  return (
    <div
      onClick={() => window.open(quote.link, "_blank")}
      className="grid lg:grid-cols-5 pill"
    >
      <p>{isoDateToString(quote.dateCreated)}</p>
      <p>{quote.name}</p>
      <p>{quote.email}</p>
      <p>{quote.phone}</p>
      <p>{quote.createdBy}</p>
    </div>
  );
};
